<div class="tle-preview-wrapper" [class.dark-preview]="darkTheme">
  <aside class="location-sidebar" aria-label="TLE locations">
    <div class="location-search">
      <mat-icon aria-hidden="true">search</mat-icon>
      <input type="search" aria-label="Search locations and sub locations"
        placeholder="Search location" [value]="searchQuery"
        (input)="searchLocations($any($event.target).value)" />
    </div>

    <div class="location-tree">
      <div *ngIf="locationsLoading" class="sidebar-state"><mat-spinner diameter="24"></mat-spinner> Loading locations…</div>
      <div *ngIf="locationsError" class="sidebar-state error-message" role="alert">{{ locationsError }}</div>
      <div *ngIf="!locationsLoading && !locationsError && !visibleParents.length" class="sidebar-state">
        <mat-icon>search_off</mat-icon><span>No locations found</span>
        <button type="button" *ngIf="searchQuery" class="text-action" (click)="searchLocations('')">Clear search</button>
      </div>

      <section *ngFor="let parent of visibleParents; trackBy: trackById" class="parent-group" [class.expanded]="isExpanded(parent)">
        <div class="parent-row">
          <button type="button" class="expand-button" [attr.aria-label]="'Expand ' + parent.name"
            [attr.aria-expanded]="isExpanded(parent)" (click)="toggleExpanded(parent)">
            <mat-icon>{{ isExpanded(parent) ? 'expand_more' : 'chevron_right' }}</mat-icon>
          </button>
          <mat-checkbox color="primary" [checked]="parentChecked(parent)" [indeterminate]="parentIndeterminate(parent)"
            [disabled]="!childCount(parent) || isFetchingNearestPasses" [aria-label]="'Select all sub locations in ' + parent.name"
            (change)="toggleParent(parent, $event.checked)"></mat-checkbox>
          <button type="button" class="parent-name" (click)="toggleExpanded(parent)" [attr.aria-expanded]="isExpanded(parent)">
            {{ parent.name }} <span class="child-count">({{ childCount(parent) }})</span>
          </button>
        </div>
        <ng-container *ngIf="isExpanded(parent)">
          <div class="parent-actions">
            <button type="button" class="text-action" (click)="toggleParent(parent, true)" [disabled]="!childCount(parent) || parentChecked(parent) || isFetchingNearestPasses">Select All</button>
            <span aria-hidden="true">|</span>
            <button type="button" class="text-action" (click)="toggleParent(parent, false)" [disabled]="(!parentChecked(parent) && !parentIndeterminate(parent)) || isFetchingNearestPasses">Clear All</button>
          </div>
          <div class="sub-location-list">
            <div *ngFor="let location of parent.subLocations; trackBy: trackById" class="sub-location-row"
              [class.active]="activeLocationId === location.id" [class.checked]="selectedIds.has(location.id)">
              <mat-checkbox color="primary" [checked]="selectedIds.has(location.id)" [disabled]="isFetchingNearestPasses"
                [matTooltip]="isFetchingNearestPasses ? 'Selection is locked while passes are being fetched' : ''"
                [aria-label]="'Show ' + location.name + ' on the map'"
                (change)="toggleLocation(location, $event.checked)"></mat-checkbox>
              <button type="button" class="location-focus" [attr.aria-pressed]="activeLocationId === location.id" (click)="focusLocation(location)">
                <span class="location-dot" [class.unselected]="!selectedIds.has(location.id)" [style.background-color]="getLocationColor(location.id)"></span>
                <span class="location-label">
                  <span class="location-name">
                    {{ location.name }}
                    <span class="location-info" *ngIf="location.description" [matTooltip]="location.description"
                      matTooltipClass="custom-option-tooltip" (click)="$event.stopPropagation()">
                      <img width="15px" [src]="tooltipImagePath" alt="info" />
                    </span>
                  </span>
                  <span class="location-coordinates">{{ formatCoordinates(location) }}</span>
                  <span class="location-status" *ngIf="selectedIds.has(location.id) && passStatus.get(location.id)"
                    [class.error-message]="passStatus.get(location.id) === 'error'">{{ statusLabel(location) }}</span>
                </span>
                <mat-icon *ngIf="activeLocationId === location.id" class="focus-indicator">my_location</mat-icon>
              </button>
            </div>
            <p *ngIf="!parent.subLocations?.length" class="empty-children">No sub locations available.</p>
          </div>
        </ng-container>
      </section>
    </div>
  </aside>

  <main class="tle-map-area" aria-label="TLE map preview">
    <div class="preview-notice" *ngIf="showPreviewNotice" role="note">
      <mat-icon aria-hidden="true">info</mat-icon>
      <span>
        TLE preview shows sensors passing near the selected location for visibility only. It does not confirm that the sensor can be tasked.
      </span>
      <button type="button" aria-label="Dismiss preview notice" (click)="showPreviewNotice = false">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <div class="selection-bar">
      <strong class="selection-label">Selected Locations ({{ selectedLocations.length }})</strong>
      <div class="selection-chips">
        <span class="selection-empty" *ngIf="!selectedLocations.length">Choose sub locations to get started</span>
        <div *ngFor="let location of selectedLocations; trackBy: trackById" class="location-chip" [style.--location-color]="getLocationColor(location.id)">
          <button type="button" class="chip-focus" (click)="focusLocation(location)">
            <span class="location-dot" [style.background-color]="getLocationColor(location.id)"></span>{{ location.name }}
          </button>
          <button type="button" class="chip-remove" [disabled]="isFetchingNearestPasses"
            [attr.aria-label]="'Remove ' + location.name" (click)="toggleLocation(location, false)"><mat-icon>close</mat-icon></button>
        </div>
      </div>
      <button mat-flat-button type="button" class="fetch-button" [disabled]="!selectedLocations.length || isFetchingNearestPasses"
        (click)="fetchNearestPasses()"><mat-icon>satellite_alt</mat-icon>{{ isFetchingNearestPasses ? 'Fetching Passes…' : 'Fetch Nearest Passes' }}</button>
    </div>

    <div #map id="tle-preview-map" class="map-canvas">
      <div class="map-status-card" *ngIf="!selectedLocations.length" (click)="$event.stopPropagation()">
        <mat-icon>location_on</mat-icon><div><strong>Your satellite preview starts here</strong><span>Select sub locations from the list, then fetch their nearest passes.</span></div>
      </div>
      <div class="fetch-progress" *ngIf="isFetchingNearestPasses" role="status" aria-live="polite" (click)="$event.stopPropagation()">
        <mat-spinner diameter="22"></mat-spinner>
        <div><strong>Fetching satellite passes</strong><span>{{ fetchCompleted }} of {{ fetchTotal }} locations completed</span></div>
        <button type="button" class="text-action" (click)="cancelSatelliteDataFetch()">Cancel</button>
      </div>

      <div #legend class="feed-overlay tle-map-legend" *ngIf="selectedLocations.length" [class.legend-below-progress]="isFetchingNearestPasses" (click)="$event.stopPropagation()" (dblclick)="$event.stopPropagation()">
        <button type="button" class="feed-legend-header" (click)="legendCollapsed = !legendCollapsed" [attr.aria-expanded]="!legendCollapsed">
          <span class="feed-legend-title">Legend</span>
          <mat-icon class="feed-legend-toggle-icon" [class.feed-legend-toggle-icon--collapsed]="legendCollapsed">expand_less</mat-icon>
        </button>
        <div class="feed-legend-items" [class.feed-legend-items--collapsed]="legendCollapsed">
          <div *ngFor="let location of selectedLocations; trackBy: trackById" class="feed-line">
            <span class="color-legend-feed" [style.background-color]="getLocationColor(location.id)"></span>
            <span class="feed-line-label"><b>{{ location.name }}</b><small class="feed-subtext">{{ statusLabel(location) }}</small></span>
          </div>
        </div>
      </div>
    </div>
  </main>
</div>
